/**
 * Audit Log Types
 * Types for audit logging system
 */

export enum AuditAction {
  // Authentication
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_FAILED = 'LOGIN_FAILED',
  PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',
  PASSWORD_RESET_COMPLETED = 'PASSWORD_RESET_COMPLETED',
  PASSWORD_CHANGED = 'PASSWORD_CHANGED',
  ACCOUNT_ACTIVATED = 'ACCOUNT_ACTIVATED',
  INVITATION_ACCEPTED = 'INVITATION_ACCEPTED',

  // User Management
  USER_CREATED = 'USER_CREATED',
  USER_INVITED = 'USER_INVITED',
  USER_UPDATED = 'USER_UPDATED',
  USER_ROLE_CHANGED = 'USER_ROLE_CHANGED',
  USER_DEACTIVATED = 'USER_DEACTIVATED',
  USER_DELETED = 'USER_DELETED',
  INVITATION_RESENT = 'INVITATION_RESENT',

  // Tenant Management (Super Admin)
  TENANT_CREATED = 'TENANT_CREATED',
  TENANT_UPDATED = 'TENANT_UPDATED',
  TENANT_DELETED = 'TENANT_DELETED',
  TENANT_SUSPENDED = 'TENANT_SUSPENDED',
  TENANT_ACTIVATED = 'TENANT_ACTIVATED',

  // OKR Operations
  OKR_CREATED = 'OKR_CREATED',
  OKR_UPDATED = 'OKR_UPDATED',
  OKR_DELETED = 'OKR_DELETED',
  OKR_STATUS_CHANGED = 'OKR_STATUS_CHANGED',
  OKR_SUBMITTED_FOR_APPROVAL = 'OKR_SUBMITTED_FOR_APPROVAL',
  OKR_APPROVED = 'OKR_APPROVED',
  OKR_REJECTED = 'OKR_REJECTED',

  // Key Result Operations
  KEY_RESULT_CREATED = 'KEY_RESULT_CREATED',
  KEY_RESULT_UPDATED = 'KEY_RESULT_UPDATED',
  KEY_RESULT_DELETED = 'KEY_RESULT_DELETED',
  KEY_RESULT_PROGRESS_UPDATED = 'KEY_RESULT_PROGRESS_UPDATED',

  // Check-in Operations
  CHECKIN_CREATED = 'CHECKIN_CREATED',
  CHECKIN_UPDATED = 'CHECKIN_UPDATED',
  CHECKIN_DELETED = 'CHECKIN_DELETED',

  // Settings & Configuration
  SETTINGS_UPDATED = 'SETTINGS_UPDATED',
  CYCLE_SETTINGS_UPDATED = 'CYCLE_SETTINGS_UPDATED',
  PERMISSIONS_UPDATED = 'PERMISSIONS_UPDATED',

  // Data Operations
  DATA_EXPORTED = 'DATA_EXPORTED',
  DATA_IMPORTED = 'DATA_IMPORTED',

  // Department Management
  DEPARTMENT_CREATED = 'DEPARTMENT_CREATED',
  DEPARTMENT_UPDATED = 'DEPARTMENT_UPDATED',
  DEPARTMENT_DELETED = 'DEPARTMENT_DELETED',

  // Cycle Management
  CYCLE_CREATED = 'CYCLE_CREATED',
  CYCLE_UPDATED = 'CYCLE_UPDATED',
  CYCLE_DELETED = 'CYCLE_DELETED',
  CYCLE_CLOSED = 'CYCLE_CLOSED',
  CYCLE_REOPENED = 'CYCLE_REOPENED',

  // Comment Operations
  COMMENT_CREATED = 'COMMENT_CREATED',
  COMMENT_UPDATED = 'COMMENT_UPDATED',
  COMMENT_DELETED = 'COMMENT_DELETED',
}

export enum AuditEntityType {
  USER = 'USER',
  TENANT = 'TENANT',
  OKR = 'OKR',
  KEY_RESULT = 'KEY_RESULT',
  CHECKIN = 'CHECKIN',
  SETTINGS = 'SETTINGS',
  DEPARTMENT = 'DEPARTMENT',
  CYCLE = 'CYCLE',
  COMMENT = 'COMMENT',
  APPROVAL = 'APPROVAL',
  EXPORT = 'EXPORT',
  IMPORT = 'IMPORT',
  AUTH = 'AUTH',
}

export interface AuditLog {
  id: string;
  tenantId: string | null;
  userId: string | null;
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string | null;
  entityName: string | null;
  oldValue: any;
  newValue: any;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: Record<string, any> | null;
  timestamp: string; // ISO date string
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  } | null;
  tenant?: {
    id: string;
    name: string;
  } | null;
}

export interface AuditLogFilters {
  userId?: string;
  action?: AuditAction;
  entityType?: AuditEntityType;
  entityId?: string;
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
  search?: string;
  limit?: number;
  offset?: number;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditStats {
  totalEvents: number;
  eventsByAction: Array<{
    action: AuditAction;
    count: number;
  }>;
  eventsByEntityType: Array<{
    entityType: AuditEntityType;
    count: number;
  }>;
}

/**
 * Helper function to get human-readable action labels
 */
export const getActionLabel = (action: AuditAction): string => {
  const labels: Record<AuditAction, string> = {
    [AuditAction.LOGIN]: 'User Login',
    [AuditAction.LOGOUT]: 'User Logout',
    [AuditAction.LOGIN_FAILED]: 'Failed Login Attempt',
    [AuditAction.PASSWORD_RESET_REQUESTED]: 'Password Reset Requested',
    [AuditAction.PASSWORD_RESET_COMPLETED]: 'Password Reset Completed',
    [AuditAction.PASSWORD_CHANGED]: 'Password Changed',
    [AuditAction.ACCOUNT_ACTIVATED]: 'Account Activated',
    [AuditAction.INVITATION_ACCEPTED]: 'Invitation Accepted',
    [AuditAction.USER_CREATED]: 'User Created',
    [AuditAction.USER_INVITED]: 'User Invited',
    [AuditAction.USER_UPDATED]: 'User Updated',
    [AuditAction.USER_ROLE_CHANGED]: 'User Role Changed',
    [AuditAction.USER_DEACTIVATED]: 'User Deactivated',
    [AuditAction.USER_DELETED]: 'User Deleted',
    [AuditAction.INVITATION_RESENT]: 'Invitation Resent',
    [AuditAction.TENANT_CREATED]: 'Tenant Created',
    [AuditAction.TENANT_UPDATED]: 'Tenant Updated',
    [AuditAction.TENANT_DELETED]: 'Tenant Deleted',
    [AuditAction.TENANT_SUSPENDED]: 'Tenant Suspended',
    [AuditAction.TENANT_ACTIVATED]: 'Tenant Activated',
    [AuditAction.OKR_CREATED]: 'OKR Created',
    [AuditAction.OKR_UPDATED]: 'OKR Updated',
    [AuditAction.OKR_DELETED]: 'OKR Deleted',
    [AuditAction.OKR_STATUS_CHANGED]: 'OKR Status Changed',
    [AuditAction.OKR_SUBMITTED_FOR_APPROVAL]: 'OKR Submitted for Approval',
    [AuditAction.OKR_APPROVED]: 'OKR Approved',
    [AuditAction.OKR_REJECTED]: 'OKR Rejected',
    [AuditAction.KEY_RESULT_CREATED]: 'Key Result Created',
    [AuditAction.KEY_RESULT_UPDATED]: 'Key Result Updated',
    [AuditAction.KEY_RESULT_DELETED]: 'Key Result Deleted',
    [AuditAction.KEY_RESULT_PROGRESS_UPDATED]: 'Key Result Progress Updated',
    [AuditAction.CHECKIN_CREATED]: 'Check-in Created',
    [AuditAction.CHECKIN_UPDATED]: 'Check-in Updated',
    [AuditAction.CHECKIN_DELETED]: 'Check-in Deleted',
    [AuditAction.SETTINGS_UPDATED]: 'Settings Updated',
    [AuditAction.CYCLE_SETTINGS_UPDATED]: 'Cycle Settings Updated',
    [AuditAction.PERMISSIONS_UPDATED]: 'Permissions Updated',
    [AuditAction.DATA_EXPORTED]: 'Data Exported',
    [AuditAction.DATA_IMPORTED]: 'Data Imported',
    [AuditAction.DEPARTMENT_CREATED]: 'Department Created',
    [AuditAction.DEPARTMENT_UPDATED]: 'Department Updated',
    [AuditAction.DEPARTMENT_DELETED]: 'Department Deleted',
    [AuditAction.CYCLE_CREATED]: 'Cycle Created',
    [AuditAction.CYCLE_UPDATED]: 'Cycle Updated',
    [AuditAction.CYCLE_DELETED]: 'Cycle Deleted',
    [AuditAction.CYCLE_CLOSED]: 'Cycle Closed',
    [AuditAction.CYCLE_REOPENED]: 'Cycle Reopened',
    [AuditAction.COMMENT_CREATED]: 'Comment Created',
    [AuditAction.COMMENT_UPDATED]: 'Comment Updated',
    [AuditAction.COMMENT_DELETED]: 'Comment Deleted',
  };
  return labels[action] || action;
};

/**
 * Helper function to get human-readable entity type labels
 */
export const getEntityTypeLabel = (entityType: AuditEntityType): string => {
  const labels: Record<AuditEntityType, string> = {
    [AuditEntityType.USER]: 'User',
    [AuditEntityType.TENANT]: 'Tenant',
    [AuditEntityType.OKR]: 'OKR',
    [AuditEntityType.KEY_RESULT]: 'Key Result',
    [AuditEntityType.CHECKIN]: 'Check-in',
    [AuditEntityType.SETTINGS]: 'Settings',
    [AuditEntityType.DEPARTMENT]: 'Department',
    [AuditEntityType.CYCLE]: 'Cycle',
    [AuditEntityType.COMMENT]: 'Comment',
    [AuditEntityType.APPROVAL]: 'Approval',
    [AuditEntityType.EXPORT]: 'Export',
    [AuditEntityType.IMPORT]: 'Import',
    [AuditEntityType.AUTH]: 'Authentication',
  };
  return labels[entityType] || entityType;
};

/**
 * Helper function to get badge color for action types
 */
export const getActionBadgeColor = (action: AuditAction): string => {
  // Security/Auth actions - red for failures, green for success
  if (action === AuditAction.LOGIN_FAILED) return 'bg-red-100 text-red-800';
  if (
    action === AuditAction.LOGIN ||
    action === AuditAction.ACCOUNT_ACTIVATED ||
    action === AuditAction.INVITATION_ACCEPTED
  )
    return 'bg-green-100 text-green-800';

  // Password actions - yellow
  if (
    action === AuditAction.PASSWORD_RESET_REQUESTED ||
    action === AuditAction.PASSWORD_RESET_COMPLETED ||
    action === AuditAction.PASSWORD_CHANGED
  )
    return 'bg-yellow-100 text-yellow-800';

  // Create actions - blue
  if (action.includes('CREATED')) return 'bg-blue-100 text-blue-800';

  // Delete actions - red
  if (action.includes('DELETED') || action.includes('DEACTIVATED'))
    return 'bg-red-100 text-red-800';

  // Update actions - indigo
  if (action.includes('UPDATED')) return 'bg-indigo-100 text-indigo-800';

  // Approval actions - purple
  if (action.includes('APPROVAL') || action.includes('APPROVED'))
    return 'bg-purple-100 text-purple-800';

  // Export/Import - teal
  if (action.includes('EXPORT') || action.includes('IMPORT'))
    return 'bg-teal-100 text-teal-800';

  // Default - gray
  return 'bg-gray-100 text-gray-800';
};
